// DelProject.js
// Creating a class to house the project data from a del form

export class DelProject {
  constructor(data = {}) {
    // The noProjectNumberChecked is only used for the new del form, and is used to determine if the checkbox is checked or not.
    this.noProjectNumberChecked = data.project_name_description || false;
    // The inactiveProjectInfo is only used for the new del form, and is used to determine if the project is inactive, and to notify the user that the project is inactive.
    this.inactiveProjectInfo = false;
    this.projectNumber = data.project_number || null;
    this.projectLabel = "";
    this.projectName = "";
    this.phaseId = data.phase_id || null;
    this.taskId = data.task_id || null;
    this.projectDescription = data.project_name_description || null;
    this.projectManager = {
      id: data.project_manager_id || null,
      name: data.project_manager_name || "",
    };
    this.invoiceManager = {
      id: data.invoice_manager_id || null,
      name: data.invoice_manager_name || "",
    };
    this.date = data.del_date || "";
    this.clientName = data.client_name || "";
    this.jsaNotRequired = data.jsa_not_required ? true : false;
    this.jsaAlreadyCompleted = data.jsa_already_completed ? true : false;
    this.wasteDisposalNotRequired =
      data.waste_disposal_not_required !== undefined
        ? data.waste_disposal_not_required
          ? true
          : false
        : true;
    this.wasteDisposalAlreadyCompleted =
      data.waste_disposal_already_completed !== undefined
        ? data.waste_disposal_already_completed
          ? true
          : false
        : true;
    this.clientSignaturesRequired = data.client_signatures_required
      ? true
      : false;

    this.projectType = data.project_type || null;

    // Some hardcoded company logic here, the JSA Version is 2 for now, but we need to account for older JSA versions.
    this.jsaVersion = data.jsa_version || 2;

    // Adding these attributes to the DelProject object so that they can be used in the project selection component.
    this.phaseName = "";
    this.availablePhases = [];

    this.taskName = "";
    this.availableTasks = [];
    // If the project is inactive, we need to add the inactive_project_info to the DelProject object.
    this.inactiveProjectInfo = data.inactive_project_info || null;
  }

  // I am not a huge fan of the below function, but for now it will work. The all project data context is passed in from the landing page, and it is used to set the project label, phase name, and available phases.
  fromExistingDel(allProjects) {
    const matchedProject = this.projectNumber
      ? allProjects.find(
          (project) => project.project_number === this.projectNumber
        )
      : null;
    this.projectLabel = matchedProject?.label || "";
    this.projectName = matchedProject?.project_name || "";
    this.availablePhases = matchedProject?.phase_list
      ? matchedProject.phase_list.map((phase) => ({
          value: phase.phase_id,
          label: phase.label,
          task_list: phase.task_list,
        }))
      : [];
    const matchedPhase = this.phaseId
      ? this.availablePhases.find((phase) => phase.value === this.phaseId)
      : null;
    this.phaseName = matchedPhase?.label || "";
    this.availableTasks = matchedPhase?.task_list
      ? matchedPhase.task_list.map((task) => ({
          value: task.task_id,
          label: task.label,
        }))
      : [];
    const matchedTask = this.taskId
      ? this.availableTasks.find((task) => task.value === this.taskId)
      : null;
    this.taskName = matchedTask?.label || "";
  }

  // This function is used to help update information in the form that is coming from an inactive project or project information. This will help keeping the application functional.
  fromExistingDelWithInactiveProject() {
    // Setting the project label to the project number, project name, and client name.
    this.projectLabel =
      this.projectNumber +
      " - " +
      this.inactiveProjectInfo.project_name +
      " - " +
      this.inactiveProjectInfo.client_name;
    this.clientName = this.inactiveProjectInfo.client_name;
    this.projectName = this.inactiveProjectInfo.project_name;
    // Find the matching phase once for reuse below.
    const matchedPhase = this.phaseId
      ? this.inactiveProjectInfo.inactive_phases.find(
          (phase) => phase.phase_id === this.phaseId
        )
      : null;
    // Setting the phase name to the phase name from the inactive project info.
    this.phaseName = matchedPhase?.phase_name || "";
    // Setting the available phases to the inactive phases from the inactive project info.
    this.availablePhases = this.inactiveProjectInfo.inactive_phases || [];
    // Setting the task name to the task name from the inactive project info.
    this.taskName =
      this.taskId && matchedPhase?.tasks
        ? matchedPhase.tasks.find((task) => task.task_id === this.taskId)
            ?.task_name || ""
        : "";
    // Setting the available tasks to the tasks from the inactive project info.
    this.availableTasks = matchedPhase?.tasks || [];
    // Setting the inactiveProjectInfo to true, so that the user knows that the project is inactive.
    this.inactiveProjectInfo = true;
  }

  clearProjectDescription() {
    this.projectDescription = "";
  }

  setProjectDescription(description) {
    this.projectDescription = description;
  }

  clearProjectInfo() {
    this.projectNumber = null;
    this.projectLabel = "";
    this.projectName = "";
    this.phaseId = null;
    this.taskId = null;
    this.projectManager = { id: null, name: "" };
    this.invoiceManager = { id: null, name: "" };
    this.clientName = "";
    this.date = "";
    this.projectType = null;
    this.inactiveProjectInfo = false;
    this.setSelectedPhase();
    this.setSelectedTask();
    this.setAvailablePhases();
  }

  setProjectInfo(project) {
    console.log("Setting project info:", project);
    if (!project) {
      this.projectNumber = null;
      this.projectLabel = "";
      this.projectName = "";
      this.inactiveProjectInfo = false;
      this.setSelectedPhase();
      this.setSelectedTask();
      this.setAvailablePhases();
      this.projectManager = { id: null, name: "" };
      this.invoiceManager = { id: null, name: "" };
      this.clientName = "";
      return;
    }
    // Setting the project number, project label, client name, and available phases.
    this.inactiveProjectInfo = false;
    this.projectNumber = project.value;
    this.projectLabel = project.label;
    this.projectName = project.project_name;
    this.clientName = project.client_name;
    this.projectType = project?.project_type || null;
    this.setAvailablePhases(project.phase_list);
    console.log("Project info set:", this);
  }

  setSelectedPhase(phase) {
    if (!phase) {
      this.phaseId = null;
      this.phaseName = "";
      this.availableTasks = [];
      this.taskId = null;
      this.taskName = "";
      return;
    }
    this.phaseId = phase.value;
    this.phaseName = phase.label;
    this.setAvailableTasks(phase);
  }

  // Function to set the available phases for the project, based on the project number.
  setAvailablePhases(phases) {
    if (!phases) {
      this.availablePhases = [];
      this.phaseId = null;
      this.taskId = null;
      return;
    }

    this.availablePhases = phases.map((phase) => ({
      value: phase.phase_id,
      label: phase.label,
      task_list: phase.task_list,
    }));
  }

  setSelectedTask(task) {
    if (!task) {
      this.taskId = null;
      this.taskName = "";
      return;
    }
    this.taskId = task.value;
    this.taskName = task.label;
  }

  // Function to set the available tasks for the project, based on the phase id and the available phases.
  setAvailableTasks(phase) {
    if (!phase || !phase.task_list) {
      this.availableTasks = [];
      this.taskId = null;
      return;
    }
    this.availableTasks = phase.task_list.map((task) => ({
      value: task.task_id,
      label: task.label,
    }));
  }

  // Update available phases and tasks from broadcast data without clearing current selections.
  // Unlike setAvailablePhases/setAvailableTasks, this preserves the current phaseId/taskId
  // since those are set independently by the broadcast's scalar fields.
  updateAvailablePhasesFromBroadcast(phaseList) {
    if (!phaseList || !Array.isArray(phaseList)) {
      this.availablePhases = [];
      this.availableTasks = [];
      return;
    }

    // Map from Redis/backend format to internal {value, label, task_list} format
    this.availablePhases = phaseList.map((phase) => ({
      value: phase.phase_id,
      label: phase.label || `${phase.phase_id} - ${phase.phase_name || ""}`,
      task_list: phase.task_list || [],
    }));

    // Update available tasks based on current phaseId
    if (this.phaseId) {
      const matchedPhase = this.availablePhases.find(
        (p) => p.value === this.phaseId
      );
      this.availableTasks = matchedPhase?.task_list
        ? matchedPhase.task_list.map((task) => ({
            value: task.task_id,
            label: task.label || `${task.task_id} - ${task.task_name || ""}`,
          }))
        : [];
    } else {
      this.availableTasks = [];
    }
  }

  // Function to set the project manager for the project. Requires the employee list to be passed in.
  setProjectManager(employees, project) {
    if (!project) {
      this.projectManager.id = null;
      this.projectManager.name = "";
      return;
    }
    this.projectManager.id = project.project_manager;
    const projectManager = employees.find(
      (emp) => emp.employee_id === this.projectManager.id
    );
    this.projectManager.name =
      projectManager?.name || "Project Manager Not Found";
  }

  setInvoiceManager(employees, project) {
    if (!project) {
      this.invoiceManager.id = null;
      this.invoiceManager.name = "";
      return;
    }
    this.invoiceManager.id = project.invoice_manager;
    const invoiceManager = employees.find(
      (emp) => emp.employee_id === this.invoiceManager.id
    );
    this.invoiceManager.name =
      invoiceManager?.name || "Invoice Manager Not Found";
  }

  setInvoiceManagerSearchInput(selected) {
    console.log("Setting invoice manager search input:", selected);
    if (!selected) {
      this.invoiceManager.id = null;
      this.invoiceManager.name = "";
      return;
    }
    this.invoiceManager.id = selected.value;
    this.invoiceManager.name = selected.label;
    console.log("Invoice manager set:", this.invoiceManager);
  }

  setNoProjectNumberChecked(checked) {
    this.noProjectNumberChecked = checked;
  }

  setProjectDate(date) {
    if (!date) {
      this.date = null;
      return;
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    this.date = `${year}-${month}-${day}`;
  }

  setJsaNotRequired(checked) {
    // This makes it backwards compatible with older JSA versions.
    this.jsaNotRequired = checked;
    this.jsaAlreadyCompleted = checked;
  }

  setWasteDisposalNotRequired(checked) {
    // This makes it backwards compatible with older JSA versions.
    this.wasteDisposalNotRequired = checked;
    this.wasteDisposalAlreadyCompleted = checked;
  }

  setClientSignaturesRequired(checked) {
    this.clientSignaturesRequired = checked;
  }

  // Helper for single and multi-field patches to be sent via websocket
  /* @Fields = {field: values}
   */
  toPatch(fields) {
    const fieldMap = {
      projectNumber: "project_number",
      phaseId: "phase_id",
      taskId: "task_id",
      projectManagerId: "project_manager_id",
      invoiceManagerId: "invoice_manager_id",
      projectDescription: "project_name_description",
      date: "del_date",
      jsaNotRequired: "jsa_not_required",
      jsaAlreadyCompleted: "jsa_already_completed",
      wasteDisposalNotRequired: "waste_disposal_not_required",
      wasteDisposalAlreadyCompleted: "waste_disposal_already_completed",
      clientSignaturesRequired: "client_signatures_required",
    };

    const patch = { patch_type: "project" };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        // Smart extraction: if value is object with .id, use the id
        if (value && typeof value === "object" && "id" in value) {
          patch[dbField] = value.id;
        } else {
          patch[dbField] = value;
        }
      }
    }
    return patch;
  }
}
